<template>
  <div
    id="players-view"
    class="pa-4"
  >
    <view-page-header all-teams />

    <section class="players-view__players mb-6">
      <v-card class="rounded-lg">
        <v-card-title>
          <v-row
            dense
            align="center"
          >
            <v-col>
              <div class="subtitle-2">
                {{ $t('table_title.all_players') }}
              </div>
            </v-col>
            <v-col cols="auto">
              <v-text-field
                v-model="playerName"
                solo
                dense
                :label="$t('table_headers.name')"
                hide-details
                clearable
                style="width: 200px"
              />
            </v-col>
            <v-col cols="auto">
              <v-select
                v-model="playerPositions.selectedId"
                solo
                dense
                :label="$t('table_headers.player_overview.position')"
                hide-details
                clearable
                item-value="id"
                item-text="name"
                :items="playerPositions.data"
                style="width: 200px"
                @change="fetchPlayers()"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerStatuses.selectedId"
                solo
                dense
                :label="$t('table_headers.player_overview.status')"
                hide-details
                clearable
                :items="playerStatuses.data"
                item-value="id"
                item-text="name"
                style="width: 200px"
                @change="fetchPlayers()"
              />
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="pa-0">
          <admin-players-list-table
            :players="filteredPlayers"
            :loading="players.loading"
            :pagination="players.pagination"
          />
        </v-card-text>
      </v-card>
    </section>

    <!--    <player-form-dialog-->
    <!--      :player="editPlayer.player"-->
    <!--      :visible.sync="editPlayer.visible"-->
    <!--      @refresh="fetchPlayers()"-->
    <!--    />-->
  </div>
</template>

<script>
import { PlayerApiService, PlayerPositionApiService, PlayerStatusApiService } from '@/data/services'
import debounce from 'lodash/debounce'
import ViewPageHeader from '@/components/ViewPageHeader'
import AdminPlayersListTable from '@/components/players/AdminPlayersListTable'
import { reactive } from '@vue/composition-api'
import { EventBus } from '@/plugins/bus'
import { toCamelCase } from '@/utils/helpers'

export default {
  name: 'AdminPlayersView',
  components: { AdminPlayersListTable, ViewPageHeader },
  setup(props) {
    const players = reactive({
      data: [],
      loading: false,
      pagination: {
        current_page: 1,
        last_page: undefined,
        per_page: 25
      }
    })

    const fetchPlayers = debounce(async function() {
      try {
        players.loading = true

        const queryParams = {
          page: players.pagination.current_page,
          per_page: players.pagination.per_page,
          player_position_id: this.playerPositions.selectedId ?? undefined,
          player_status_id: this.playerStatuses.selectedId ?? undefined,
          team_id: this.currentTeamId === 'all' ? undefined : this.currentTeamId
        }

        const resp = await PlayerApiService.getPaginated(queryParams)

        players.data = resp.data
        players.pagination = resp.meta
      } catch (e) {
        players.data = []
        players.pagination.current_page = 1
        players.pagination.last_page = 1
      } finally {
        players.loading = false
      }
    }, 500)

    return {
      players,
      fetchPlayers,
      editPlayer: reactive({
        player: undefined,
        visible: false
      }),
      playerStatuses: {
        data: [],
        loading: false,
        loaded: false,
        selectedId: undefined
      },
      playerPositions: {
        data: [],
        loading: false,
        loaded: false,
        selectedId: undefined
      },
      playerName: ''
    }
  },
  computed: {
    currentTeamId() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM].id
    },
    filteredPlayers() {
      if (!this.playerName) return this.players.data

      return this.players.data.filter((player) => {
        return player.name.toLowerCase().includes(this.playerName.toLowerCase())
      })
    }
  },
  watch: {
    'players.pagination.current_page': function (newVal, oldVal) {
      this.fetchPlayers()
    },
    currentTeamId() {
      this.players.pagination = {
        current_page: 1,
        last_page: undefined,
        per_page: 25
      }
      this.fetchPlayers()
    }
  },
  created() {
    this.players.loading = true

    this.fetchPlayers()
    this.fetchPlayerStatuses()
    this.fetchPlayerPositions()

    EventBus.$on(this.$events.PLAYER_CREATED, () => this.fetchPlayers())
    EventBus.$on(this.$events.PLAYER_UPDATED, () => this.fetchPlayers())
    EventBus.$on(this.$events.PLAYER_DELETED, () => this.fetchPlayers())
  },
  methods: {
    initPlayersTablePagination() {
      this.players.pagination.current_page = typeof this.$route.query.page === 'string' ? parseInt(this.$route.query.page) : 1
    },
    onPlayerRowClicked(playerId) {
      this.$router.push({ name: this.$routeNames.PLAYER_DETAILS, params: { id: playerId } })
    },
    onEditPlayer(player) {
      EventBus.$emit(this.$events.EDIT_PLAYER, player)
    },
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    }
  }
}
</script>
